import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { LoadingScreen, ButtonSpinner } from '../components/ui/Spinner';
import { ArrowLeftIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, UserCircleIcon, PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { formatCurrency, formatDateTime } from '../utils/formatting';
import PlayerTypeIcon from '../components/PlayerTypeIcon';

const HAND_TAG_CHOICES = [
    { value: 'discipline_fold', label: 'Discipline Fold', color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' },
    { value: 'curiosity_call', label: 'Curiosity Call', color: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' },
    { value: 'cooler', label: 'Cooler', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    { value: 'big_pot_won', label: 'Big Pot Won', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
    { value: 'bad_beat', label: 'Bad Beat', color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' },
    { value: 'hero_call', label: 'Hero Call', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
    { value: 'bluff_success', label: 'Successful Bluff', color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' },
    { value: 'bluff_fail', label: 'Failed Bluff', color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300' },
    { value: 'review_needed', label: 'Review Needed', color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' },
];

const DECISION_QUALITY_CHOICES = [
    { value: 'correct', label: 'Correct', color: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
    { value: 'mistake', label: 'Mistake', color: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
    { value: 'neutral', label: 'Neutral', color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
    { value: 'pending', label: 'Pending Review', color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
];

const SEVERITY_CHOICES = [
    { value: 'minor', label: 'Minor' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'severe', label: 'Severe' },
];

const HAND_TYPE_COLORS = {
    'spot': 'border-blue-500',
    'fold': 'border-green-500',
    'call': 'border-red-500',
    'bluff': 'border-purple-500',
    'cooler': 'border-orange-500',
    'other': 'border-gray-500',
};

function HandReviewCard({ item, leaks, sessionPlayers, onSave, onCreateLeak }) {
    const hand = item.data;
    const [analysis, setAnalysis] = useState(hand.review_analysis || '');
    const [selectedLeaks, setSelectedLeaks] = useState(hand.leaks?.map(l => l.id) || []);
    const [decisionQuality, setDecisionQuality] = useState(hand.decision_quality || 'pending');
    const [severity, setSeverity] = useState(hand.severity || '');
    const [saving, setSaving] = useState(false);
    const [showNewLeakForm, setShowNewLeakForm] = useState(false);
    const [newLeakName, setNewLeakName] = useState('');
    const [newLeakCategory, setNewLeakCategory] = useState('strategic');

    // Edit mode state
    const [isEditing, setIsEditing] = useState(false);
    const [editDescription, setEditDescription] = useState(hand.hand_description || '');
    const [editOutcome, setEditOutcome] = useState(hand.outcome || '');
    const [editAmount, setEditAmount] = useState(hand.amount || '');
    const [editNotes, setEditNotes] = useState(hand.notes || '');
    const [editTags, setEditTags] = useState(hand.tags || []);
    const [editDifficulty, setEditDifficulty] = useState(hand.difficulty || '');
    const [editDrillable, setEditDrillable] = useState(hand.drillable || false);
    const [editPlayersInvolved, setEditPlayersInvolved] = useState(hand.players_involved?.map(p => p.id) || []);

    // Sync local state when hand prop changes (after save/refetch)
    useEffect(() => {
        setAnalysis(hand.review_analysis || '');
        setSelectedLeaks(hand.leaks?.map(l => l.id) || []);
        setDecisionQuality(hand.decision_quality || 'pending');
        setSeverity(hand.severity || '');
        setEditDescription(hand.hand_description || '');
        setEditOutcome(hand.outcome || '');
        setEditAmount(hand.amount || '');
        setEditNotes(hand.notes || '');
        setEditTags(hand.tags || []);
        setEditDifficulty(hand.difficulty || '');
        setEditDrillable(hand.drillable || false);
        setEditPlayersInvolved(hand.players_involved?.map(p => p.id) || []);
    }, [hand.id, hand.decision_quality, hand.review_analysis, hand.severity, hand.reviewed]);

    const getTagColor = (tagValue) => {
        const tag = HAND_TAG_CHOICES.find(t => t.value === tagValue);
        return tag?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    };

    const getTagLabel = (tagValue) => {
        const tag = HAND_TAG_CHOICES.find(t => t.value === tagValue);
        return tag?.label || tagValue;
    };

    const getDecisionQualityColor = (value) => {
        const choice = DECISION_QUALITY_CHOICES.find(c => c.value === value);
        return choice?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    };

    const handleSave = async (markReviewed = true) => {
        setSaving(true);
        try {
            // Unified Hand endpoint
            const payload = {
                hand_description: editDescription,
                outcome: editOutcome,
                amount: editAmount || null,
                notes: editNotes,
                tags: editTags,
                difficulty: editDifficulty,
                review_analysis: analysis,
                decision_quality: decisionQuality,
                severity: decisionQuality === 'mistake' ? severity : null,
                drillable: editDrillable,
                leaks_ids: selectedLeaks,
                players_involved_ids: editPlayersInvolved,
            };

            // Only mark as reviewed if explicitly requested
            if (markReviewed) {
                payload.reviewed = true;
            }

            await axios.patch(`/api/hands/${hand.id}/`, payload);
            setIsEditing(false);
            onSave();
        } catch (err) {
            console.error('Failed to save review:', err);
        } finally {
            setSaving(false);
        }
    };

    const handleCreateLeak = async () => {
        if (!newLeakName.trim()) return;
        try {
            const response = await axios.post('/api/leaks/', {
                name: newLeakName,
                category: newLeakCategory
            });
            onCreateLeak(response.data);
            setSelectedLeaks([...selectedLeaks, response.data.id]);
            setShowNewLeakForm(false);
            setNewLeakName('');
        } catch (err) {
            console.error('Failed to create leak:', err);
        }
    };

    return (
        <div className={`bg-white dark:bg-gray-800 rounded-lg border-l-4 ${HAND_TYPE_COLORS[hand.hand_type] || 'border-gray-500'} shadow-sm`}>
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {hand.hand_type_display}
                        </span>
                        {hand.decision_quality && hand.decision_quality !== 'pending' && (
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${getDecisionQualityColor(hand.decision_quality)}`}>
                                {hand.decision_quality_display}
                            </span>
                        )}
                        {hand.severity && (
                            <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                                {hand.severity_display}
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setIsEditing(!isEditing)}
                            className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors ${
                                isEditing
                                    ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                        >
                            {isEditing ? <XMarkIcon className="w-3 h-3" /> : <PencilIcon className="w-3 h-3" />}
                            {isEditing ? 'Cancel Edit' : 'Edit'}
                        </button>
                        {hand.reviewed && (
                            <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                                Reviewed
                            </span>
                        )}
                    </div>
                </div>
                {/* Show linked leaks */}
                {hand.leaks && hand.leaks.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {hand.leaks.map(leak => (
                            <span key={leak.id} className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-0.5 rounded">
                                {leak.name}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Hand Details */}
            <div className="p-4 space-y-4">
                {/* Description - Editable */}
                <div>
                    {isEditing ? (
                        <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                Hand Description
                            </label>
                            <textarea
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                rows={4}
                                placeholder="Describe the hand action..."
                            />
                        </div>
                    ) : (
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                            {hand.hand_description}
                        </p>
                    )}
                </div>

                {/* Outcome/Amount - Always editable now */}
                <div className="flex items-center gap-4">
                    {isEditing ? (
                        <>
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Outcome</label>
                                <select
                                    value={editOutcome}
                                    onChange={(e) => setEditOutcome(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">Select...</option>
                                    <option value="won">Won</option>
                                    <option value="lost">Lost</option>
                                    <option value="chopped">Chopped</option>
                                    <option value="folded">Folded</option>
                                    <option value="unknown">Unknown</option>
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Amount ($)</label>
                                <input
                                    type="number"
                                    value={editAmount}
                                    onChange={(e) => setEditAmount(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    placeholder="0.00"
                                    step="0.01"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Difficulty</label>
                                <select
                                    value={editDifficulty}
                                    onChange={(e) => setEditDifficulty(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    <option value="">Select...</option>
                                    <option value="easy">Easy</option>
                                    <option value="hard">Hard</option>
                                </select>
                            </div>
                        </>
                    ) : (
                        hand.outcome && (
                            <span className={`text-sm font-medium ${hand.outcome === 'won' ? 'text-green-600' : hand.outcome === 'lost' ? 'text-red-600' : 'text-gray-600'}`}>
                                {hand.outcome_display}: {hand.amount ? formatCurrency(Math.abs(parseFloat(hand.amount))) : 'N/A'}
                            </span>
                        )
                    )}
                </div>

                {/* Tags */}
                <div>
                    {isEditing ? (
                        <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Tags</label>
                            <div className="flex flex-wrap gap-2">
                                {HAND_TAG_CHOICES.map(tag => (
                                    <label
                                        key={tag.value}
                                        className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs cursor-pointer transition-colors ${
                                            editTags.includes(tag.value)
                                                ? tag.color + ' border border-current'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={editTags.includes(tag.value)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setEditTags([...editTags, tag.value]);
                                                } else {
                                                    setEditTags(editTags.filter(t => t !== tag.value));
                                                }
                                            }}
                                            className="sr-only"
                                        />
                                        {tag.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : (
                        hand.tags && hand.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                                {hand.tags.map(tag => (
                                    <span key={tag} className={`text-xs px-2 py-0.5 rounded ${getTagColor(tag)}`}>
                                        {getTagLabel(tag)}
                                    </span>
                                ))}
                            </div>
                        )
                    )}
                </div>

                {/* In-session notes - Editable */}
                <div>
                    {isEditing ? (
                        <div>
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                In-session Notes
                            </label>
                            <textarea
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                rows={2}
                                placeholder="Notes from during the session..."
                            />
                        </div>
                    ) : (
                        hand.notes && (
                            <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">In-session notes:</p>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    {hand.notes}
                                </p>
                            </div>
                        )
                    )}
                </div>

                {/* Players involved - editable */}
                <div className="space-y-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Players involved:</span>
                    <div className="flex items-center gap-2 flex-wrap">
                        {sessionPlayers && sessionPlayers.map(sp => {
                            const isSelected = editPlayersInvolved.includes(sp.player.id);
                            return (
                                <button
                                    key={sp.player.id}
                                    onClick={() => {
                                        if (isSelected) {
                                            setEditPlayersInvolved(editPlayersInvolved.filter(id => id !== sp.player.id));
                                        } else {
                                            setEditPlayersInvolved([...editPlayersInvolved, sp.player.id]);
                                        }
                                    }}
                                    className={`flex items-center gap-1.5 px-2 py-1 rounded-full transition-colors ${
                                        isSelected
                                            ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 ring-2 ring-primary-500'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    {sp.player.profile_picture_url ? (
                                        <img
                                            src={sp.player.profile_picture_url}
                                            alt={sp.player.name}
                                            className="w-5 h-5 rounded-full object-cover"
                                        />
                                    ) : (
                                        <PlayerTypeIcon playerType={sp.player.player_category} size="w-4 h-4" />
                                    )}
                                    <span className="text-xs font-medium">{sp.player.name}</span>
                                </button>
                            );
                        })}
                        {(!sessionPlayers || sessionPlayers.length === 0) && (
                            <span className="text-xs text-gray-400 italic">No players logged for this session</span>
                        )}
                    </div>
                </div>

                {/* Drillable checkbox when editing */}
                {isEditing && (
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={editDrillable}
                            onChange={(e) => setEditDrillable(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Flag for future drilling/study
                        </span>
                    </label>
                )}

                {/* Save Changes button when editing */}
                {isEditing && (
                    <button
                        onClick={() => handleSave(false)}
                        disabled={saving}
                        className="w-full px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {saving ? <ButtonSpinner /> : <CheckIcon className="w-4 h-4" />}
                        Save Changes
                    </button>
                )}
            </div>

            {/* Review Section */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 space-y-4">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Review Analysis
                </div>

                {/* Decision Quality */}
                <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Decision Quality
                    </label>
                    <div className="flex flex-wrap gap-2">
                        {DECISION_QUALITY_CHOICES.map(choice => (
                            <button
                                key={choice.value}
                                onClick={() => setDecisionQuality(choice.value)}
                                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                    decisionQuality === choice.value
                                        ? choice.color + ' ring-2 ring-offset-2 ring-current'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                {choice.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Severity - only shown when mistake */}
                {decisionQuality === 'mistake' && (
                    <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-2">
                            Severity
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {SEVERITY_CHOICES.map(choice => (
                                <button
                                    key={choice.value}
                                    onClick={() => setSeverity(choice.value)}
                                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                                        severity === choice.value
                                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-2 ring-offset-2 ring-red-500'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    {choice.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Thought Process */}
                <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                        What was your thought process? What went wrong/right? How to improve?
                    </label>
                    <textarea
                        value={analysis}
                        onChange={(e) => setAnalysis(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        rows={3}
                        placeholder="Analyze this hand..."
                    />
                </div>

                {/* Leak Selection - Multi-select */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs text-gray-500 dark:text-gray-400">
                            Link to leaks (select all that apply)
                        </label>
                        <button
                            onClick={() => setShowNewLeakForm(!showNewLeakForm)}
                            className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
                        >
                            <PlusIcon className="w-3 h-3" />
                            New Leak
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {leaks.map(leak => (
                            <label
                                key={leak.id}
                                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs cursor-pointer transition-colors ${
                                    selectedLeaks.includes(leak.id)
                                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={selectedLeaks.includes(leak.id)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedLeaks([...selectedLeaks, leak.id]);
                                        } else {
                                            setSelectedLeaks(selectedLeaks.filter(id => id !== leak.id));
                                        }
                                    }}
                                    className="sr-only"
                                />
                                {leak.name}
                            </label>
                        ))}
                        {leaks.length === 0 && (
                            <span className="text-xs text-gray-400">No leaks defined yet</span>
                        )}
                    </div>
                </div>

                {/* New Leak Form */}
                {showNewLeakForm && (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg p-3 space-y-2">
                        <input
                            type="text"
                            value={newLeakName}
                            onChange={(e) => setNewLeakName(e.target.value)}
                            placeholder="Leak name (e.g., 'Overfolding to 3bets')"
                            className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <div className="flex gap-2">
                            <select
                                value={newLeakCategory}
                                onChange={(e) => setNewLeakCategory(e.target.value)}
                                className="flex-1 px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="strategic">Strategic</option>
                                <option value="mental">Mental</option>
                                <option value="preflop">Preflop</option>
                                <option value="postflop">Postflop</option>
                                <option value="tilt">Tilt</option>
                            </select>
                            <button
                                onClick={handleCreateLeak}
                                className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
                            >
                                Create
                            </button>
                        </div>
                    </div>
                )}

                {/* Save Button */}
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                    {saving ? <ButtonSpinner /> : <CheckIcon className="w-4 h-4" />}
                    Mark as Reviewed
                </button>
            </div>
        </div>
    );
}

function PlayerUpdateCard({ sessionPlayer, onUpdate }) {
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [addedNotes, setAddedNotes] = useState([]); // Track notes added in this session

    const handleSave = async () => {
        if (!notes.trim()) return;
        setSaving(true);
        try {
            // Append to player's history_notes
            const player = sessionPlayer.player;
            const timestamp = new Date().toLocaleDateString();
            const updatedNotes = player.history_notes
                ? `${player.history_notes}\n\n[${timestamp}] ${notes}`
                : `[${timestamp}] ${notes}`;

            await axios.patch(`/api/players/${player.id}/`, {
                history_notes: updatedNotes
            });

            // Add to local list of added notes
            setAddedNotes(prev => [...prev, { text: notes, timestamp }]);
            setNotes('');
            onUpdate();
        } catch (err) {
            console.error('Failed to update player:', err);
        } finally {
            setSaving(false);
        }
    };

    const player = sessionPlayer.player;

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            {/* Clickable player header */}
            <Link
                to={`/players/${player?.id}`}
                className="flex items-center gap-3 mb-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 -mx-2 -mt-2 p-2 rounded-lg transition-colors"
            >
                {/* Player picture */}
                {player?.profile_picture_url ? (
                    <img
                        src={player.profile_picture_url}
                        alt={player.name}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-gray-600"
                    />
                ) : (
                    <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center">
                        <UserCircleIcon className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                )}
                <div className="flex-1">
                    <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400">
                            {player?.name}
                        </p>
                        <PlayerTypeIcon playerType={player?.player_category} size="w-4 h-4" />
                    </div>
                    <p className="text-xs text-gray-500">Seat {sessionPlayer.seat_position || '?'}</p>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-gray-400" />
            </Link>

            {/* Notes added in this review session */}
            {addedNotes.length > 0 && (
                <div className="mb-3 space-y-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Notes added:</p>
                    {addedNotes.map((note, idx) => (
                        <div key={idx} className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-2">
                            <p className="text-sm text-green-800 dark:text-green-300">{note.text}</p>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1">{note.timestamp}</p>
                        </div>
                    ))}
                </div>
            )}

            <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add observations from this session..."
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-2"
                rows={2}
            />

            <button
                onClick={handleSave}
                disabled={saving || !notes.trim()}
                className="px-4 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
            >
                {saving ? 'Saving...' : 'Add to Profile'}
            </button>
        </div>
    );
}

export default function SessionReviewPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [reviewData, setReviewData] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [reviewNotes, setReviewNotes] = useState('');
    const [completing, setCompleting] = useState(false);
    const [activeTab, setActiveTab] = useState('hands'); // 'hands' | 'players' | 'summary'

    const fetchReviewData = useCallback(async () => {
        try {
            const response = await axios.get(`/api/sessions/${id}/review-data/`);
            setReviewData(response.data);
            setReviewNotes(response.data.review_notes || '');
        } catch (err) {
            console.error('Failed to load review data:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchReviewData();
    }, [fetchReviewData]);

    const handleLeakCreated = (newLeak) => {
        setReviewData(prev => ({
            ...prev,
            leaks: [...prev.leaks, newLeak]
        }));
    };

    const handleCompleteReview = async () => {
        setCompleting(true);
        try {
            await axios.post(`/api/sessions/${id}/mark-reviewed/`, {
                review_notes: reviewNotes
            });
            navigate(`/sessions/${id}`);
        } catch (err) {
            console.error('Failed to complete review:', err);
        } finally {
            setCompleting(false);
        }
    };

    if (loading) return <LoadingScreen />;
    if (!reviewData) return <div className="p-4 text-red-500">Failed to load review data</div>;

    const currentItem = reviewData.review_items[currentIndex];
    const reviewedCount = reviewData.review_items.filter(item => item.data.reviewed).length;
    const progress = reviewData.total_items > 0 ? (reviewedCount / reviewData.total_items) * 100 : 0;

    return (
        <div className="min-h-screen bg-[#faf8f5] dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => navigate(`/sessions/${id}`)}
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        >
                            <ArrowLeftIcon className="w-5 h-5" />
                            <span>Back to Session</span>
                        </button>
                        <div className="text-right">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                Session Review
                            </p>
                            <p className="text-xs text-gray-500">
                                {reviewData.session_location} - {formatDateTime(reviewData.session_date)}
                            </p>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-4">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span>{reviewedCount} of {reviewData.total_items} hands reviewed</span>
                            <span className={reviewData.session_profit >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {formatCurrency(reviewData.session_profit)}
                            </span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-primary-600 transition-all duration-300"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-4 mt-4 border-b border-gray-200 dark:border-gray-700 -mb-px">
                        {['hands', 'players', 'summary'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                                    activeTab === tab
                                        ? 'border-primary-600 text-primary-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab === 'hands' && `Hands (${reviewData.total_items})`}
                                {tab === 'players' && `Players (${reviewData.session_players.length})`}
                                {tab === 'summary' && 'Summary'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-6">
                {/* Hands Tab */}
                {activeTab === 'hands' && reviewData.total_items > 0 && (
                    <div className="space-y-4">
                        {/* Navigation */}
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
                                disabled={currentIndex === 0}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 disabled:opacity-50"
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                                Previous
                            </button>
                            <span className="text-sm text-gray-500">
                                Hand {currentIndex + 1} of {reviewData.total_items}
                            </span>
                            <button
                                onClick={() => setCurrentIndex(Math.min(reviewData.total_items - 1, currentIndex + 1))}
                                disabled={currentIndex === reviewData.total_items - 1}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 disabled:opacity-50"
                            >
                                Next
                                <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Current Hand Card */}
                        {currentItem && (
                            <HandReviewCard
                                key={`${currentItem.type}-${currentItem.id}`}
                                item={currentItem}
                                leaks={reviewData.leaks}
                                sessionPlayers={reviewData.session_players}
                                onSave={fetchReviewData}
                                onCreateLeak={handleLeakCreated}
                            />
                        )}

                        {/* Quick Jump */}
                        <div className="flex flex-wrap gap-1 justify-center">
                            {reviewData.review_items.map((item, idx) => (
                                <button
                                    key={`${item.type}-${item.id}`}
                                    onClick={() => setCurrentIndex(idx)}
                                    className={`w-8 h-8 text-xs rounded ${
                                        idx === currentIndex
                                            ? 'bg-primary-600 text-white'
                                            : item.data.reviewed
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                    }`}
                                >
                                    {idx + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'hands' && reviewData.total_items === 0 && (
                    <div className="text-center py-12 text-gray-500">
                        No hands logged for this session.
                    </div>
                )}

                {/* Players Tab */}
                {activeTab === 'players' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {reviewData.session_players.map(sp => (
                            <PlayerUpdateCard
                                key={sp.id}
                                sessionPlayer={sp}
                                onUpdate={fetchReviewData}
                            />
                        ))}
                        {reviewData.session_players.length === 0 && (
                            <div className="col-span-2 text-center py-12 text-gray-500">
                                No players logged for this session.
                            </div>
                        )}
                    </div>
                )}

                {/* Summary Tab */}
                {activeTab === 'summary' && (
                    <div className="space-y-6">
                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{reviewedCount}/{reviewData.total_items}</p>
                                <p className="text-xs text-gray-500">Hands Reviewed</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                <p className={`text-2xl font-bold ${reviewData.session_profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {formatCurrency(reviewData.session_profit)}
                                </p>
                                <p className="text-xs text-gray-500">Session Result</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                <p className="text-2xl font-bold text-red-600">{reviewData.mistakes_count || 0}</p>
                                <p className="text-xs text-gray-500">Mistakes</p>
                            </div>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 text-center">
                                <p className="text-2xl font-bold text-green-600">{reviewData.correct_count || 0}</p>
                                <p className="text-xs text-gray-500">Correct</p>
                            </div>
                        </div>

                        {/* In-Session Notes */}
                        {reviewData.session_notes && reviewData.session_notes.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                                <h3 className="font-medium text-gray-900 dark:text-white mb-3">
                                    📝 Notes from Session ({reviewData.session_notes.length})
                                </h3>
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {reviewData.session_notes.map((note) => (
                                        <div key={note.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border-l-4 border-blue-500">
                                            <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{note.content}</p>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                {new Date(note.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Takeaways */}
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-4">
                            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Session Takeaways</h3>
                            <textarea
                                value={reviewNotes}
                                onChange={(e) => setReviewNotes(e.target.value)}
                                placeholder="What did you learn from this session? Key takeaways, things to work on..."
                                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                rows={5}
                            />
                        </div>

                        {/* Complete Button */}
                        <button
                            onClick={handleCompleteReview}
                            disabled={completing}
                            className="w-full px-6 py-3 text-base font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                            {completing ? <ButtonSpinner /> : <CheckIcon className="w-5 h-5" />}
                            Complete Review
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
